import React, { createContext, useContext, useEffect, useState } from 'react';
import svc from './services/LifeAssistantService';
import { PrivacyPolicyContent } from './PrivacyPolicy';
import { TermsOfServiceContent } from './TermsOfService';
import './AuthGate.css';
import './PrivacyPolicy.css';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function LandingScreen({ onLogin, onSignup }) {
  return (
    <div className="ag-card ag-landing">
      <h1 className="ag-title">Life Assistant</h1>
      <p className="ag-tagline">
        Seu painel pessoal de saúde, treino, alimentação, agenda e finanças
        — tudo organizado num só lugar, alimentado pelos seus próprios
        dados (Apple Health, Google, Microsoft) e com sugestões por IA.
      </p>
      <button className="ag-btn" type="button" onClick={onSignup}>Criar conta</button>
      <button className="ag-link ag-landing-login" type="button" onClick={onLogin}>Já tenho conta — Entrar</button>
      <p className="ag-switch">
        <a href="/privacidade">Política de Privacidade</a>
      </p>
    </div>
  );
}

function LoginForm({ onSwitch, onLoggedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await svc.login(email.trim(), password);
      if (r.error) throw new Error(r.error);
      onLoggedIn(r);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const forgotPassword = async () => {
    if (!email.trim()) { setError('Digite seu e-mail primeiro'); return; }
    setBusy(true);
    try {
      await svc.forgotPassword(email.trim());
      setForgotSent(true);
    } finally { setBusy(false); }
  };

  return (
    <form className="ag-card" onSubmit={submit}>
      <h1 className="ag-title">Entrar</h1>
      {error && <p className="ag-error">{error}</p>}
      {forgotSent && <p className="ag-info">Se esse e-mail existir, enviamos um link pra redefinir a senha.</p>}
      <input className="ag-input" type="email" placeholder="E-mail" value={email}
        onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
      <input className="ag-input" type="password" placeholder="Senha" value={password}
        onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
      <button className="ag-btn" type="submit" disabled={busy}>{busy ? '…' : 'Entrar'}</button>
      <button type="button" className="ag-link" onClick={forgotPassword}>Esqueci minha senha</button>
      <p className="ag-switch">
        Não tem conta? <button type="button" className="ag-link" onClick={onSwitch}>Cadastre-se</button>
      </p>
    </form>
  );
}

function SignupForm({ onSwitch }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await svc.signup(email.trim(), password, name.trim(), acceptedPolicy);
      if (r.error) throw new Error(r.error);
      setDone(true);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="ag-card">
        <h1 className="ag-title">Quase lá</h1>
        <p className="ag-info">
          Mandamos um e-mail de confirmação pra <b>{email}</b>. Clique no link pra ativar sua conta e poder entrar.
        </p>
        <button type="button" className="ag-link" onClick={onSwitch}>Voltar pro login</button>
      </div>
    );
  }

  return (
    <form className="ag-card" onSubmit={submit}>
      <h1 className="ag-title">Criar conta</h1>
      {error && <p className="ag-error">{error}</p>}
      <input className="ag-input" placeholder="Seu nome" value={name}
        onChange={(e) => setName(e.target.value)} autoComplete="name" />
      <input className="ag-input" type="email" placeholder="E-mail" value={email}
        onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
      <input className="ag-input" type="password" placeholder="Senha (mín. 8 caracteres)" value={password}
        onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
      <label className="ag-policy-check">
        <input
          type="checkbox" checked={acceptedPolicy}
          onChange={(e) => setAcceptedPolicy(e.target.checked)}
        />
        <span>
          Li e aceito a{' '}
          <a href="/privacidade" target="_blank" rel="noreferrer">Política de Privacidade</a>
        </span>
      </label>
      <button className="ag-btn" type="submit" disabled={busy || !acceptedPolicy}>{busy ? '…' : 'Cadastrar'}</button>
      <p className="ag-switch">
        Já tem conta? <button type="button" className="ag-link" onClick={onSwitch}>Entrar</button>
      </p>
    </form>
  );
}

function VerifyEmailScreen({ token, onGoLogin }) {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${svc.baseUrl}/api/auth/verify-email?token=${encodeURIComponent(token)}`);
        const d = await r.json();
        setStatus(d.error ? 'error' : 'ok');
      } catch { setStatus('error'); }
    })();
  }, [token]);

  return (
    <div className="ag-card">
      <h1 className="ag-title">Confirmação de e-mail</h1>
      {status === 'checking' && <p className="ag-info">Confirmando…</p>}
      {status === 'ok' && <p className="ag-info">E-mail confirmado! Já pode entrar.</p>}
      {status === 'error' && <p className="ag-error">Link inválido ou já usado.</p>}
      <button type="button" className="ag-btn" onClick={onGoLogin}>Ir pro login</button>
    </div>
  );
}

function ResetPasswordScreen({ token, onGoLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await svc.resetPassword(token, password);
      if (r.error) throw new Error(r.error);
      setDone(true);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="ag-card">
        <h1 className="ag-title">Senha redefinida</h1>
        <p className="ag-info">Já pode entrar com a nova senha.</p>
        <button type="button" className="ag-btn" onClick={onGoLogin}>Ir pro login</button>
      </div>
    );
  }

  return (
    <form className="ag-card" onSubmit={submit}>
      <h1 className="ag-title">Nova senha</h1>
      {error && <p className="ag-error">{error}</p>}
      <input className="ag-input" type="password" placeholder="Nova senha (mín. 8 caracteres)" value={password}
        onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
      <button className="ag-btn" type="submit" disabled={busy}>{busy ? '…' : 'Salvar nova senha'}</button>
    </form>
  );
}

function AuthGate({ children }) {
  const [state, setState] = useState('loading');
  const [user, setUser] = useState(null);

  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get('token');

  const goLogin = () => {
    window.history.replaceState({}, '', '/');
    setState('login');
  };

  useEffect(() => {
    if (path === '/privacidade' || path === '/termos') return;
    if (path === '/verificar-email' && tokenFromUrl) { setState('verify'); return; }
    if (path === '/redefinir-senha' && tokenFromUrl) { setState('reset'); return; }
    (async () => {
      const me = await svc.me();
      if (me && !me.error) { setUser(me); setState('authenticated'); }
      else setState('landing');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try { await svc.logout(); } catch { /* silêncio */ }
    setUser(null);
    setState('landing');
  };

  // Rota pública, sem exigir login — precisa ficar acessível de fora do app
  // (verificação do app OAuth do Google, e qualquer visitante antes de criar conta).
  if (path === '/privacidade') {
    return (
      <div className="ag-wrap">
        <div className="pp-standalone">
          <h1>Política de Privacidade</h1>
          <PrivacyPolicyContent />
        </div>
      </div>
    );
  }

  if (path === '/termos') {
    return (
      <div className="ag-wrap">
        <div className="pp-standalone">
          <h1>Termos de Serviço</h1>
          <TermsOfServiceContent />
        </div>
      </div>
    );
  }

  if (state === 'loading') {
    return <div className="ag-wrap"><p className="ag-info">Carregando…</p></div>;
  }

  if (state === 'verify') {
    return <div className="ag-wrap"><VerifyEmailScreen token={tokenFromUrl} onGoLogin={goLogin} /></div>;
  }

  if (state === 'reset') {
    return <div className="ag-wrap"><ResetPasswordScreen token={tokenFromUrl} onGoLogin={goLogin} /></div>;
  }

  if (state === 'landing') {
    return (
      <div className="ag-wrap">
        <LandingScreen onLogin={() => setState('login')} onSignup={() => setState('signup')} />
      </div>
    );
  }

  if (state === 'login') {
    return (
      <div className="ag-wrap">
        <LoginForm onSwitch={() => setState('signup')} onLoggedIn={(u) => { setUser(u); setState('authenticated'); }} />
      </div>
    );
  }

  if (state === 'signup') {
    return <div className="ag-wrap"><SignupForm onSwitch={() => setState('login')} /></div>;
  }

  return (
    <AuthContext.Provider value={{ user, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthGate;
